import { useForm } from "react-hook-form";
import { BaseFormControlledModal } from "../../../../../../common/components/base_form_controlled_modal"
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { FormRadioGroupInput } from "../../../../../../common/components/masked_inputs/react_forms/form_radio_group_input";
import { DefaultFormDatePickerInput } from "../../../../../../common/components/default_form_date_picker_input";
import { DefaultFormAutocomplete } from "../../../../../../common/components/default_form_autocomplete";
import { AssistenciaTecnicaAtendimentoLista } from "../../../../domain/models/assistencia_tecnica_atendimento_lista";
import { RegistroAtendimentoForm, RegistroAtendimentoSchema } from "../../../schemas/AssistenciaTecnica_cadastro/registro_atendimento";
import { FuncionarioDropDown } from "../../../../../../common/domain/models/funcionario_dropdown";

import DefaultFormInput from "../../../../../../common/components/default_form_input";

/**
 * RegistroAtendimentoModal
 */

type Props = {
  open: boolean;
  saving: boolean;
  loading: boolean;
  funcionariosResponsavelDropdown: FuncionarioDropDown[];
  selectedRegistroAtendimento: AssistenciaTecnicaAtendimentoLista | null;
  onClose: () => void;
  onSubmit: (form: RegistroAtendimentoForm) => void;
};

export const RegistroAtendimentoModal = ({
  open,
  saving,
  loading,
  funcionariosResponsavelDropdown,
  selectedRegistroAtendimento,
  onClose,
  onSubmit,
}: Props) => {

  const hookForm = useForm<RegistroAtendimentoForm>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    resolver: zodResolver(RegistroAtendimentoSchema),
  });

  const tipoOptions = useMemo(() => {
    return [
      {
        id: 0,
        label: 'Vistoria',
        style: {
          backgroundColor: '#89c4f4',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
      {
        id: 1,
        label: 'Reparo',
        style: {
          backgroundColor: '#f3565d',
          color: 'white',
          padding: '.2rem .4rem',
          fontSize: '0.85rem',
          fontWeight: 700,
          textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
          borderRadius: '2px',
        },
      },
    ];
  }, [
  ]);

  useEffect(() => {
    if (open) {
      if (selectedRegistroAtendimento) {
        hookForm.setValue('tipo', selectedRegistroAtendimento.tipo ?? 0);
        hookForm.setValue('descricao', selectedRegistroAtendimento.descricao ?? '');
        hookForm.setValue('dataInicio', selectedRegistroAtendimento.dataInicio ? new Date(selectedRegistroAtendimento.dataInicio) : null);
        hookForm.setValue('dataTermino', selectedRegistroAtendimento.dataTermino ? new Date(selectedRegistroAtendimento.dataTermino) : null);
        hookForm.setValue('observacoes', selectedRegistroAtendimento.observacoes);
        hookForm.setValue('responsavelConstrutora', selectedRegistroAtendimento.idFuncionario);
        hookForm.setValue('responsavelCliente', selectedRegistroAtendimento.responsavelCliente);
      } else {
        hookForm.reset({ tipo: 0 });
      }
    }
  }, [
    open,
    selectedRegistroAtendimento,
    hookForm,
  ]);

  return (
    <BaseFormControlledModal
      open={open}
      saving={saving}
      loading={loading}
      title="Registro de Atendimento"
      onClose={onClose}
      onSubmit={hookForm.handleSubmit(onSubmit)}>
      <div className="w-full flex flex-col gap-4 my-4">
        <FormRadioGroupInput
          name='tipo'
          label='Tipo de Atendimento'
          options={tipoOptions}
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='descricao'
          label='Descrição da Atividades'
          required={true}
          multiline={true}
          rows={5}
          reactHookForm={hookForm}
        />

        <DefaultFormDatePickerInput
          name='dataInicio'
          label='Data Inicio'
          reactHookForm={hookForm}
        />

        <DefaultFormDatePickerInput
          name='dataTermino'
          label='Data Termino'
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='observacoes'
          label='Observações'
          multiline={true}
          rows={5}
          reactHookForm={hookForm}
        />

        <DefaultFormAutocomplete
          name='responsavelConstrutora'
          label='Responsável Construtora'
          options={funcionariosResponsavelDropdown}
          getOptionDisabled={x => !x.canBeSelected}
          getOptionKey={x => x.idFuncionario}
          getOptionLabel={x => x.nome}
          reactHookForm={hookForm}
        />

        <DefaultFormInput
          name='responsavelCliente'
          label='Responsável Cliente'
          reactHookForm={hookForm}
        />
      </div>
    </BaseFormControlledModal>
  );
}